/**
 * Keep the page itself from scrolling in iOS Safari while a full-screen
 * new-experience screen is up (see hooks/useShellChrome). The screen is
 * deliberately taller than the visible viewport — the overhang is what runs
 * under the glass toolbar — and `overflow: hidden` on html/body does not stop
 * a touch pan in iOS Safari.
 *
 * iOS honours preventDefault on the FIRST touchmove only: once it has begun a
 * scroll, later moves arrive with `cancelable: false`. So the decision is made
 * on where the finger is, never on which way it is going (a first move is
 * often a pixel or two of jitter):
 *   · inside a horizontal scroller (the chip row), a sideways move passes;
 *   · inside a vertical scroller that actually overflows (a long list), the
 *     move passes — the list takes it, and overscroll-behavior keeps a pull
 *     past its end inside the list;
 *   · anywhere else (the header, a short list, empty space) it is cancelled.
 * CSS touch-action says the same thing up front (PeopleScreen.css).
 */

const overflows = (el, axis) => {
  const style = window.getComputedStyle(el);
  const o = axis === 'x' ? style.overflowX : style.overflowY;
  if (o !== 'auto' && o !== 'scroll') return false;
  return axis === 'x' ? el.scrollWidth > el.clientWidth + 1 : el.scrollHeight > el.clientHeight + 1;
};

/** The nearest ancestor of `target` (inside the page) that scrolls on `axis`, or null. */
export const scrollerFor = (target, axis = 'y') => {
  for (
    let el = target;
    el && el !== document.body && el !== document.documentElement;
    el = el.parentElement
  ) {
    if (el.nodeType === 1 && overflows(el, axis)) return el;
  }
  return null;
};

/**
 * Install the guard. Returns the uninstall function.
 * Non-passive on purpose — iOS only honours preventDefault from a
 * `passive: false` touchmove listener.
 *
 * @param {number} [holdAt=0] - the page scroll the backstop puts back: 0 keeps
 *   the strip behind the clock a flat colour; a bleed keeps a card behind it
 */
export const lockPageScroll = (holdAt = 0) => {
  let startX = 0;
  let startY = 0;
  const onStart = (e) => {
    if (e.touches.length !== 1) return;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  };
  const onMove = (e) => {
    if (e.touches.length !== 1 || !e.cancelable) return; // pinch is the browser's
    const dx = Math.abs(e.touches[0].clientX - startX);
    const dy = Math.abs(e.touches[0].clientY - startY);
    if (dx > dy && scrollerFor(e.target, 'x')) return; // the chip row
    if (scrollerFor(e.target, 'y')) return; // a list with more to show
    e.preventDefault();
  };
  // Backstop: anything that still moves the page (a focus scroll) is put back
  // — the strip behind the clock depends on the scroll being exactly holdAt.
  const onScroll = () => {
    if (window.scrollY !== holdAt) window.scrollTo(0, holdAt);
  };
  document.addEventListener('touchstart', onStart, { passive: true });
  document.addEventListener('touchmove', onMove, { passive: false });
  window.addEventListener('scroll', onScroll, { passive: true });
  // Back on after a stand-down (a field closed, Quick Add shut): put the page back.
  onScroll();
  return () => {
    document.removeEventListener('touchstart', onStart);
    document.removeEventListener('touchmove', onMove);
    window.removeEventListener('scroll', onScroll);
  };
};
